import AuthAPI from '../api/auth';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import DashboardAudioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    const { websocketURL = '' } = window.chatwootConfig || {};
    super(app, pubsubToken, websocketURL);
    this.CancelTyping = [];
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.created': this.onConversationCreated,
      'conversation.status_changed': this.onStatusChange,
      'user:logout': this.onLogout,
      'page:reload': this.onReload,
      'assignee.changed': this.onAssigneeChanged,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.contact_changed': this.onConversationContactChange,
      'presence.update': this.onPresenceUpdate,
      'contact.deleted': this.onContactDelete,
      'contact.updated': this.onContactUpdate,
      'conversation.mentioned': this.onConversationMentioned,
      'notification.created': this.onNotificationCreated,
      'notification.deleted': this.onNotificationDeleted,
      'notification.updated': this.onNotificationUpdated,
      'first.reply.created': this.onFirstReplyCreated,
      'conversation.read': this.onConversationRead,
      'conversation.updated': this.onConversationUpdated,
      'account.cache_invalidated': this.onCacheInvalidate,
      // diag/llamadas_whatsapp (F5): llamadas de WhatsApp
      'voice_call.incoming': this.onVoiceCall('onIncoming'),
      'voice_call.accepted': this.onVoiceCall('onAccepted'),
      'voice_call.ended': this.onVoiceCall('onEnded'),
      'voice_call.outbound_connected': this.onVoiceCall('onOutboundConnected'),
      'voice_call.outbound_accepted': this.onVoiceCall('onOutboundAccepted'),
      'voice_call.permission_granted': this.onVoiceCall('onPermissionGranted'),
    };
  }

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT);
  };

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_DISCONNECT);
  };

  isAValidEvent = data => {
    return this.app.$store.getters.getCurrentAccountId === data.account_id;
  };

  // diag/llamadas_whatsapp: solo los de la cuenta abierta; cada evento a su acción.
  onVoiceCall = action => data => {
    if (!this.isAValidEvent(data)) return;
    this.app.$store.dispatch(`whatsappCalls/${action}`, data);
  };

  onMessageUpdated = data => {
    this.app.$store.dispatch('updateMessage', data);
  };

  onPresenceUpdate = data => {
    this.app.$store.dispatch('contacts/updatePresence', data.contacts);
    this.app.$store.dispatch('agents/updatePresence', data.users);
    this.app.$store.dispatch('setCurrentUserAvailability', data.users);
  };

  onConversationContactChange = payload => {
    const { meta = {}, id: conversationId } = payload;
    const { sender } = meta || {};
    if (conversationId) {
      this.app.$store.dispatch('updateConversationContact', {
        conversationId,
        ...sender,
      });
    }
  };

  onAssigneeChanged = payload => {
    const { id } = payload;
    if (id) {
      this.app.$store.dispatch('updateConversation', payload);
    }
    this.fetchConversationStats();
  };

  onConversationCreated = data => {
    this.app.$store.dispatch('addConversation', data);
    this.fetchConversationStats();
  };

  onConversationRead = data => {
    this.app.$store.dispatch('updateConversation', data);
  };

  // eslint-disable-next-line class-methods-use-this
  onLogout = () => AuthAPI.logout();

  onMessageCreated = data => {
    const {
      conversation: { last_activity_at: lastActivityAt },
      conversation_id: conversationId,
    } = data;
    DashboardAudioNotificationHelper.onNewMessage(data);
    this.app.$store.dispatch('addMessage', data);
    // feat/chat_interno_pulido: contador del menú «Chat interno» y sonido.
    this.app.$store.dispatch('internalChat/messageCreated', data);
    if (lastActivityAt && conversationId) {
      this.app.$store.dispatch('updateConversationLastActivity', {
        lastActivityAt,
        conversationId,
      });
    }
  };

  // eslint-disable-next-line class-methods-use-this
  onReload = () => window.location.reload();

  onStatusChange = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onConversationUpdated = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onTypingOn = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/create', {
      conversationId,
      user,
    });
    this.initTimer({ conversation, user });
  };

  onTypingOff = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/destroy', {
      conversationId,
      user,
    });
  };

  onConversationMentioned = data => {
    this.app.$store.dispatch('addMentions', data);
  };

  clearTimer = conversationId => {
    const timerEvent = this.CancelTyping[conversationId];

    if (timerEvent) {
      clearTimeout(timerEvent);
      this.CancelTyping[conversationId] = null;
    }
  };

  initTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    // Turn off typing automatically after 30 seconds
    this.CancelTyping[conversationId] = setTimeout(() => {
      this.onTypingOff({ conversation, user });
    }, 30000);
  };

  // eslint-disable-next-line class-methods-use-this
  fetchConversationStats = () => {
    emitter.emit('fetch_conversation_stats');
    emitter.emit('fetch_overview_reports');
  };

  onContactDelete = data => {
    this.app.$store.dispatch(
      'contacts/deleteContactThroughConversations',
      data.id
    );
    this.fetchConversationStats();
  };

  onContactUpdate = data => {
    this.app.$store.dispatch('contacts/updateContact', data);
  };

  onNotificationCreated = data => {
    this.app.$store.dispatch('notifications/addNotification', data);
    this.maybeToastTaskNotification(data);
  };

  // @tickets_cases F3/F4 — además de la campanita, saca un toast en tiempo real
  // para tipos de tarea en lista blanca (asignación y completado). Lista blanca,
  // no "todas": convertir cada notificación en toast cambiaría el producto entero.
  maybeToastTaskNotification = data => {
    const TOAST_MAP = {
      case_ticket_assignment: {
        key: 'CASE_TICKETS.NOTIFICATIONS.TOAST_TICKET_ASSIGNED',
        icon: 'person-add',
      },
      case_task_assignment: {
        key: 'CASE_TICKETS.TASKS.INBOX.TOAST_ASSIGNED',
        icon: 'person-add',
      },
      case_task_completed: {
        key: 'CASE_TICKETS.TASKS.INBOX.TOAST_COMPLETED',
        icon: 'checkmark-circle',
      },
      // Plan-Plantilla-Tareas-Etapa — tareas nuevas de la etapa y etapa lista.
      case_stage_tasks: {
        key: 'CASE_TICKETS.TASKS.INBOX.TOAST_STAGE_TASKS',
        icon: 'checkmark-circle',
      },
      case_stage_ready: {
        key: 'CASE_TICKETS.TASKS.INBOX.TOAST_STAGE_READY',
        icon: 'checkmark-circle',
      },
    };
    const n = data && data.notification;
    const conf = n && TOAST_MAP[n.notification_type];
    if (!conf) return;

    const task = n.push_message_title || '';
    const folio = (n.primary_actor && n.primary_actor.folio) || '';
    // Canal propio del módulo (verde, a la derecha), no el Snackbar global.
    emitter.emit('caseToastMessage', {
      message: this.app.$t(conf.key, { task, folio }),
      icon: conf.icon,
    });
  };

  onNotificationDeleted = data => {
    this.app.$store.dispatch('notifications/deleteNotification', data);
  };

  onNotificationUpdated = data => {
    this.app.$store.dispatch('notifications/updateNotification', data);
  };

  // eslint-disable-next-line class-methods-use-this
  onFirstReplyCreated = () => {
    emitter.emit('fetch_overview_reports');
  };

  onCacheInvalidate = data => {
    const keys = data.cache_keys;
    this.app.$store.dispatch('labels/revalidate', { newKey: keys.label });
    this.app.$store.dispatch('inboxes/revalidate', { newKey: keys.inbox });
    this.app.$store.dispatch('teams/revalidate', { newKey: keys.team });
  };
}

export default {
  init(pubsubToken) {
    return new ActionCableConnector(window.WOOT, pubsubToken);
  },
};
